import React, { useState, useEffect } from 'react';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Heading } from '@/components/Typography';
import Chip from '@/components/Chip';
import LoadingState from '@/components/LoadingState';
import { Worker, AuditRecord } from '@/types/onboardingBypass';
import { OnboardingBypassAPI } from '@/services/onboardingBypassAPI';
import { format } from 'date-fns';

interface WorkerDetailModalProps {
  worker: Worker;
  open: boolean;
  onClose: () => void;
  onBypassClick: () => void;
}

const WorkerDetailModal: React.FC<WorkerDetailModalProps> = ({
  worker,
  open,
  onClose,
  onBypassClick,
}) => {
  const [auditHistory, setAuditHistory] = useState<AuditRecord[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && worker) {
      fetchAuditHistory();
    }
  }, [open, worker]);

  const fetchAuditHistory = async () => {
    setLoading(true);
    try {
      const response = await OnboardingBypassAPI.getWorkerAuditTrail(
        worker.id,
        { per_page: 5 },
      );
      if (response.success) {
        setAuditHistory(response.data.audits);
      }
    } catch (error) {
      console.error('Error fetching audit history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStepStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return '✅';
      case 'pending':
        return '⏳';
      case 'under_review':
        return '🔄';
      case 'rejected':
        return '❌';
      default:
        return '⏸️';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch (e) {
      return dateString;
    }
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '800px',
          maxHeight: '90vh',
          overflow: 'auto',
          width: '90%',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Stack gap={16} vertical>
          {/* Header */}
          <Stack gap={16} justify="apart" align="center">
            <Heading>Worker Details: {worker.name}</Heading>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={onBypassClick}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Bypass Worker
              </button>
              <button
                onClick={onClose}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </Stack>

          {/* Worker Information */}
          <Card title="Worker Information">
            <Card.Section>
              <Stack gap={20}>
                <Stack gap={8} vertical style={{ flex: 1 }}>
                  <Body weight="medium">Name:</Body>
                  <Body>{worker.name}</Body>
                  <Body weight="medium">Background Status:</Body>
                  <Chip
                    color={
                      (worker as any).background_status === 'approved'
                        ? 'success'
                        : (worker as any).background_status === 'pending'
                          ? 'warning'
                          : (worker as any).background_status === 'rejected'
                            ? 'error'
                            : 'default'
                    }
                    label={((worker as any).background_status || 'Not Started')
                      .replace(/_/g, ' ')
                      .toUpperCase()}
                    size="small"
                  />
                </Stack>
              </Stack>
            </Card.Section>
          </Card>

          {/* Bypass Information */}
          {worker.bypass_info && (
            <Card title="Bypass Information">
              <Card.Section>
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: '#fff2f2',
                    border: '1px solid #ffcccc',
                    borderRadius: '6px',
                  }}
                >
                  <Stack gap={8} vertical>
                    <Body>
                      <strong>Bypassed:</strong>{' '}
                      {formatDate(worker.bypass_info.bypassed_at)}
                    </Body>
                    <Body>
                      <strong>By:</strong> {worker.bypass_info.bypassed_by_user}
                    </Body>
                    <Body>
                      <strong>Reason:</strong>{' '}
                      {worker.bypass_info.bypass_remarks}
                    </Body>
                  </Stack>
                </div>
              </Card.Section>
            </Card>
          )}

          {/* Raw Onboarding JSON */}
          {(worker as any).onboarding && (
            <Card title="Raw Onboarding JSON">
              <Card.Section>
                <div
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e1e5e9',
                    borderRadius: '4px',
                    padding: '12px',
                    maxHeight: '400px',
                    overflow: 'auto',
                  }}
                >
                  <pre
                    style={{
                      margin: 0,
                      fontSize: '12px',
                      fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {JSON.stringify((worker as any).onboarding, null, 2)}
                  </pre>
                </div>
              </Card.Section>
            </Card>
          )}

          {/* Recent Activity */}
          <Card title="Recent Activity">
            <Card.Section>
              {loading ? (
                <Stack
                  align="center"
                  justify="center"
                  style={{ padding: '20px 0' }}
                >
                  <LoadingState
                    overlayColor="white"
                    text="Loading audit history..."
                  />
                </Stack>
              ) : auditHistory.length > 0 ? (
                <Stack gap={8} vertical>
                  {auditHistory.map((audit) => (
                    <div
                      key={audit.id}
                      style={{
                        padding: '8px 0',
                        borderBottom: '1px solid #e1e5e9',
                      }}
                    >
                      <Body>
                        {formatDate(audit.created_at)}: {audit.admin_user.name}{' '}
                        changed status from{' '}
                        <strong>{audit.previous_state || 'initial'}</strong> to{' '}
                        <strong>{audit.new_state}</strong>
                      </Body>
                      {audit.reason && (
                        <Body color="lighter">Reason: {audit.reason}</Body>
                      )}
                    </div>
                  ))}
                </Stack>
              ) : (
                <Body color="lighter">No recent activity found.</Body>
              )}
            </Card.Section>
          </Card>
        </Stack>
      </div>
    </div>
  );
};

export default WorkerDetailModal;
